import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v3";
import { Copy, Check, Shield, User } from "lucide-react";
import type { ModalComponentProps } from "@/lib/modal";
import { Button } from "@/components/ui/button";
import {
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateInvite } from "@/lib/api/teams";
import type { TenantRole, InviteRole } from "@/lib/api/types";
import { useSubscriptionError } from "@/hooks/use-subscription-error";

const roleOptions = [
  {
    value: "member" as const,
    label: "Member",
    icon: User,
    description: "Basic team access",
  },
  {
    value: "admin" as const,
    label: "Admin",
    icon: Shield,
    description: "Can manage members and invitations",
    ownerOnly: true,
  },
];

const inviteFormSchema = z.object({
  role: z.enum(["admin", "member"]),
});

type InviteFormValues = z.infer<typeof inviteFormSchema>;

export interface InviteTeamMemberModalProps {
  tenantId: string;
  currentUserRole: TenantRole;
}

export function InviteTeamMemberModal({
  tenantId,
  currentUserRole,
  onClose,
}: InviteTeamMemberModalProps & ModalComponentProps) {
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createInvite = useCreateInvite(tenantId);
  const { handleError } = useSubscriptionError({
    tenantId,
    fallbackMessage: "Failed to create invitation",
  });

  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: { role: "member" },
  });

  const onSubmit = async (values: InviteFormValues) => {
    createInvite.mutate(
      { role: values.role as InviteRole },
      {
        onSuccess: (data) => {
          const baseUrl = window.location.origin;
          setInviteLink(`${baseUrl}/invite/${data.data.invite.token}`);
        },
        onError: (error) => handleError(error),
      }
    );
  };

  const handleCopy = async () => {
    if (!inviteLink) return;

    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const availableRoles = roleOptions.filter(
    (r) => !r.ownerOnly || currentUserRole === "owner"
  );

  return (
    <>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>Create invitation</ResponsiveModalTitle>
        <ResponsiveModalDescription>
          Create a single-use invitation link to add a new team member.
        </ResponsiveModalDescription>
      </ResponsiveModalHeader>

      {!inviteLink ? (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <User className="size-4 text-rose-500" />
                    Role
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableRoles.map((r) => {
                        const Icon = r.icon;
                        return (
                          <SelectItem key={r.value} value={r.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="size-4" />
                              <span>{r.label}</span>
                              <span className="text-muted-foreground text-xs">
                                - {r.description}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <ResponsiveModalFooter>
              <Button type="submit" disabled={createInvite.isPending}>
                {createInvite.isPending ? "Creating..." : "Create invitation"}
              </Button>
            </ResponsiveModalFooter>
          </form>
        </Form>
      ) : (
        <>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Invitation link</Label>
              <div className="flex gap-2">
                <Input
                  value={inviteLink}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  className="shrink-0"
                  aria-label="Copy invite link"
                >
                  {copied ? (
                    <Check className="size-4 text-green-500" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </Button>
              </div>
              <p className="text-muted-foreground text-xs">
                This link can only be used once and expires in 7 days.
              </p>
            </div>
          </div>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={onClose}>
              Done
            </Button>
          </ResponsiveModalFooter>
        </>
      )}
    </>
  );
}

InviteTeamMemberModal.displayName = "InviteTeamMemberModal";
